import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { KeepAwake } from '@capacitor-community/keep-awake';

/**
 * Whether the screen can be kept awake on this platform.
 *
 * Native apps always use the Capacitor KeepAwake plugin (Android WebView has no
 * `navigator.wakeLock`); on the web it depends on the Screen Wake Lock API.
 */
export function isKeepAwakeSupported(): boolean {
  return Capacitor.isNativePlatform() || 'wakeLock' in navigator;
}

interface UseKeepAwakeOptions {
  /** Called if the wake lock can't be acquired (request rejected, plugin error, or unsupported). */
  onError?: (error: unknown) => void;
}

/**
 * Keeps the screen awake while `enabled` is true.
 *
 * Uses Capacitor's KeepAwake plugin on native platforms and the Screen Wake Lock
 * API on the web. On the web the browser releases the lock whenever the page is
 * hidden, so it is re-requested when the page becomes visible again.
 *
 * The lock is released when `enabled` becomes false or the component unmounts.
 *
 * Usage:
 * ```tsx
 * const [cookMode, setCookMode] = useState(false);
 * useKeepAwake(cookMode, { onError: () => setCookMode(false) });
 * ```
 */
export function useKeepAwake(enabled: boolean = true, { onError }: UseKeepAwakeOptions = {}) {
  // Kept in a ref so a new callback identity doesn't re-run the effect and drop the lock
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const isNative = Capacitor.isNativePlatform();
    let isActive = true;
    let sentinel: WakeLockSentinel | null = null;

    const requestWebLock = async () => {
      const lock = await navigator.wakeLock.request('screen');
      if (!isActive) {
        // Switched off while the request was pending — don't leak the lock
        await lock.release();
        return;
      }
      sentinel = lock;
      // The browser releases the lock itself when the page is hidden; forget it
      // so it can be re-requested when the page is visible again
      lock.addEventListener('release', () => {
        if (sentinel === lock) sentinel = null;
      });
    };

    const enableWakeLock = async () => {
      try {
        if (isNative) {
          await KeepAwake.keepAwake();
        } else if ('wakeLock' in navigator) {
          await requestWebLock();
        } else {
          throw new Error('Screen Wake Lock API not supported');
        }
      } catch (error) {
        console.warn('Failed to enable wake lock:', error);
        if (isActive) onErrorRef.current?.(error);
      }
    };

    const handleVisibilityChange = async () => {
      if (!isActive || isNative || sentinel || document.visibilityState !== 'visible') return;
      try {
        await requestWebLock();
      } catch (error) {
        // Not surfaced to the user — the lock will be retried on the next visibility change
        console.warn('Wake lock re-request failed:', error);
      }
    };

    enableWakeLock();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isActive = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      const release = isNative ? KeepAwake.allowSleep() : sentinel?.release();
      sentinel = null;
      Promise.resolve(release).catch((error) => {
        console.error('Failed to disable wake lock:', error);
      });
    };
  }, [enabled]);
}
